import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Grid,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Divider,
    Paper,
    CircularProgress,
    Tooltip,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ImageIcon from "@mui/icons-material/Image";

const AddSetOffOlympiad = ({ closePopup, modalData }) => {

    // ================= STATES =================

    const [oldData, setOldData] = useState([]);
    const [attachmentData, setAttachmentData] = useState([]);
    const [previewImage, setPreviewImage] = useState(null);

    const [loading, setLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);

    const [formData, setFormData] = useState({
        amount: "",
        remark: ""
    });

    // ================= GET OLD DATA =================

    useEffect(() => {
        if (modalData?.regId) {
            getOldData();
        }
    }, [modalData?.regId]);

    const getOldData = async () => {
        try {
            setHistoryLoading(true);

            const result =
                await window.Platform.database.getSOBOforOlympiadByRegId({
                    regId: modalData?.regId,
                });

            if (result?.status) {
                setOldData(result?.data || []);
            } else {
                setOldData([]);
            }
        } catch (error) {
            console.log(error);
            setOldData([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    // ================= HANDLE INPUT =================

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // ================= HANDLE FILES =================

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);

        if (!files?.length) return;

        const mappedFiles = files.map((file) => ({
            file,
            name: file.name,
            type: file.type,
            preview: URL.createObjectURL(file),
        }));

        setAttachmentData((prev) => [...prev, ...mappedFiles]);
    };

    // ================= REMOVE ATTACHMENT =================

    const handleRemoveAttachment = (indexToRemove) => {
        const isConfirm = window.confirm(
            "Are you sure you want to remove this attachment?"
        );

        if (!isConfirm) return;

        setAttachmentData((prev) =>
            prev.filter((_, index) => index !== indexToRemove)
        );
    };

    // ================= VALIDATION =================

    const validateForm = () => {
        if (!formData.amount) {
            window.NotificationUtils.showError( "Please enter amount" );
            return false;
        }
        if( formData?.amount == 0){
             window.NotificationUtils.showError( "Amount must not be 0 " );
            return false;
        }

        if (!formData.remark || formData?.remark == "") {
            window.NotificationUtils.showError( "Please enter remark" );
            return false;
        }
         if (formData.remark?.length < 20) {
            window.NotificationUtils.showError( "Remark must be 20 characters" );
            return false;
        }

        return true;
    };

    // ================= SUBMIT =================

    const handleSubmit = async () => {
        try {

            if (!validateForm()) return;

            setLoading(true);

            const payload = {
                "regId":modalData?.regId,
                "set_off_amount": formData?.amount,
                "set_off_remark": formData?.remark
            }

            const result = await window.Platform.database.updateSetOffAmount( payload ); 

            if (result?.status) {

                window.NotificationUtils.showSuccess(
                    result?.message || "Added successfully"
                );

                getOldData();

                setFormData({
                    amount: "",
                    remark: ""
                });

                setAttachmentData([]);

                closePopup();

            } else {

                window.NotificationUtils.showError(
                    result?.message || "Something went wrong"
                );
            }

        } catch (error) {

            console.log(error);

            window.NotificationUtils.showError(
                "Something went wrong"
            );

        } finally {
            setLoading(false);
        }
    };

    // ================= TRANSACTION SUMMARY =================

    const transactionSummary = (modalData?.transactions || []).reduce(
        (acc, item) => {
            acc.totalAmount += Number(item?.totalAmount || 0);
            acc.paidAmount += Number(item?.paidAmount || 0);

            return acc;
        },
        {
            totalAmount: 0,
            paidAmount: 0,
        }
    );

    // actual pending = total - paid
    const actualPending =
        transactionSummary?.totalAmount -
        transactionSummary?.paidAmount;



    return (
        <Box p={3} position="relative">

            {/* ================= HEADER ================= */}

            <Typography
                style={{ fontWeight: "700", display: "flex", justifyContent: "center", alignItems: "center", borderBottom: "1px solid #000" }}
                mb={1}
                className="text-center"
            >
                <span style={{ fontSize: "20px", fontWeight: "700" }}>Set Off: - </span>
                ( <span> <b>RNO ID:</b> {modalData?.rno_id}</span> )
            </Typography>

           <div style={{ borderBottom: "1px solid #000" }}>
             <div className="row">
                <div className="col-md-4">
                    <b>RM Name:</b> {modalData?.userName}

                </div>
                <div className="col-md-8">
                    <b>Party Name:</b>{" "} {modalData?.schoolName} ({modalData?.partyId})
                </div>
            </div>
            <div className="row mt-2">
                <div className="col-md-4">

                    <b>Total Amount:</b>{" "}
                    ₹{transactionSummary?.totalAmount || 0}
                </div>
                <div className="col-md-4">
                    <b>Paid Amount:</b>{" "}
                    ₹{transactionSummary?.paidAmount || 0}
                </div>
                <div className="col-md-4">
                    <b>Pending Amount:</b>{" "}
                    ₹{actualPending || 0}
                </div>
            </div>
           </div>


            {/* <IconButton
                onClick={closePopup}
                sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                }}
            >
                <CloseIcon />
            </IconButton> */}
            <Paper
                elevation={3}
                sx={{
                    p: 3,
                    borderRadius: 3,
                }}
            >
                <Grid container spacing={3}>

                    <Grid item xs={12} md={12}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            label="Set Off Amount"
                            name="amount"
                            value={
                                formData.amount
                            }
                            onChange={handleChange}
                        />
                    </Grid>

                    {/* Amount */}
                    <Grid item xs={12} md={12}>
                        <TextField
                            fullWidth
                            label="Remark"
                            name="remark"
                            value={
                                formData.remark
                            }
                            onChange={handleChange}
                        />
                    </Grid>




                    <Grid item xs={12}>
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            gap={2}
                            mt={2}
                        >
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={closePopup}
                                disabled={loading}
                            >
                                Cancel
                            </Button>

                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? (
                                    <CircularProgress
                                        size={22}
                                        color="inherit"
                                    />
                                ) : (
                                    "Submit"
                                )}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

export default AddSetOffOlympiad;